import React, { useState, useEffect } from 'react';
import {
  Card,
  Button,
  Table,
  Space,
  Progress,
  Tag,
  Statistic,
  Row,
  Col,
  Modal,
  message,
  Typography,
  Alert,
  Badge,
} from 'antd';
import {
  PlayCircleOutlined,
  StepForwardOutlined,
  StopOutlined,
  UserOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  MinusCircleOutlined,
} from '@ant-design/icons';
import LatexText from '../components/LatexText';
import { useParams, useNavigate } from 'react-router-dom';
import {
  collection,
  doc,
  onSnapshot,
  query,
  where,
  addDoc,
  updateDoc,
  getDocs,
} from 'firebase/firestore';
import { db, COLLECTIONS } from '../config/firebase';
import axios from 'axios';

const { Title, Text } = Typography;
const API_URL = process.env.REACT_APP_API_URL || 'https://api.calloctech.com/api';

interface LiveTestSession {
  id: string;
  live_test_id: string;
  teacher_id: string;
  teacher_name: string;
  status: 'waiting' | 'in_progress' | 'completed';
  current_question_index: number;
  current_question_id: string | null;
  started_at: any;
  ended_at: any;
  total_participants: number;
  question_start_time: any;
}

interface Participant {
  id: string;
  student_name: string;
  student_email: string;
  joined_at: any;
  total_answered: number;
  is_active: boolean;
}

export default function LiveTestControl() {
  const { liveTestId } = useParams<{ liveTestId: string }>();
  const navigate = useNavigate();

  const [liveTest, setLiveTest] = useState<any>(null);
  const [session, setSession] = useState<LiveTestSession | null>(null);
  const [participants, setParticipants] = useState<Participant[]>([]);
  const [questions, setQuestions] = useState<any[]>([]);
  const [currentQuestion, setCurrentQuestion] = useState<any>(null);
  const [responseCount, setResponseCount] = useState(0);
  const [responses, setResponses] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (liveTestId) {
      loadLiveTest();
      checkExistingSession();
    }
  }, [liveTestId]);

  useEffect(() => {
    if (session?.id) {
      // Subscribe to participants
      const participantsQuery = query(
        collection(db, COLLECTIONS.LIVE_TEST_PARTICIPANTS),
        where('session_id', '==', session.id)
      );

      const unsubscribeParticipants = onSnapshot(participantsQuery, (snapshot) => {
        const participantsList: Participant[] = [];
        snapshot.forEach((doc) => {
          participantsList.push({ id: doc.id, ...doc.data() } as Participant);
        });
        setParticipants(participantsList);
      });

      // Subscribe to responses for current question
      if (session.current_question_id) {
        const responsesQuery = query(
          collection(db, COLLECTIONS.LIVE_TEST_RESPONSES),
          where('session_id', '==', session.id),
          where('question_id', '==', session.current_question_id)
        );

        const unsubscribeResponses = onSnapshot(responsesQuery, (snapshot) => {
          const docs = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
          setResponseCount(snapshot.size);
          setResponses(docs);
        });

        return () => {
          unsubscribeParticipants();
          unsubscribeResponses();
        };
      }

      return () => {
        unsubscribeParticipants();
      };
    }
  }, [session?.id, session?.current_question_id]);

  const loadLiveTest = async () => {
    try {
      const docRef = doc(db, COLLECTIONS.LIVE_TESTS, liveTestId!);
      const unsubscribe = onSnapshot(docRef, (snapshot) => {
        if (snapshot.exists()) {
          const data = { id: snapshot.id, ...snapshot.data() } as any;
          setLiveTest(data);

          // Load questions from Firestore
          if (data.questions && data.questions.length > 0) {
            setQuestions(data.questions);
          }
        }
      });

      return unsubscribe;
    } catch (error) {
      message.error('Failed to load live test');
    }
  };

  const checkExistingSession = async () => {
    try {
      const sessionsQuery = query(
        collection(db, COLLECTIONS.LIVE_TEST_SESSIONS),
        where('live_test_id', '==', liveTestId),
        where('status', 'in', ['waiting', 'in_progress'])
      );

      const unsubscribe = onSnapshot(sessionsQuery, (snapshot) => {
        if (!snapshot.empty) {
          const sessionData = snapshot.docs[0];
          const updatedSession = { id: sessionData.id, ...sessionData.data() } as LiveTestSession;
          setSession(updatedSession);
          
          // Update current question when session changes
          if (updatedSession.current_question_id && questions.length > 0) {
            const question = questions.find(q => q.id === updatedSession.current_question_id);
            if (question) {
              setCurrentQuestion(question);
            }
          }
        }
      });

      return unsubscribe;
    } catch (error) {
      console.error('Error checking session:', error);
    }
  };

  const handleStartTest = async () => {
    try {
      setLoading(true);

      const sessionData = {
        live_test_id: liveTestId,
        teacher_id: 'teacher_id', // Get from auth
        teacher_name: 'Teacher Name', // Get from auth
        status: 'waiting',
        current_question_index: -1,
        current_question_id: null,
        started_at: new Date(),
        ended_at: null,
        total_participants: 0,
        question_start_time: null,
      };

      const docRef = await addDoc(collection(db, COLLECTIONS.LIVE_TEST_SESSIONS), sessionData);

      // Update live test status
      await updateDoc(doc(db, COLLECTIONS.LIVE_TESTS, liveTestId!), {
        status: 'live',
        updated_at: new Date(),
      });

      setSession({ id: docRef.id, ...sessionData } as LiveTestSession);
      message.success('Live test started! Students can now join.');
    } catch (error) {
      console.error('Error starting test:', error);
      message.error('Failed to start test');
    } finally {
      setLoading(false);
    }
  };

  const handleNextQuestion = async () => {
    if (!session || !questions.length) return;

    const nextIndex = session.current_question_index + 1;

    if (nextIndex >= questions.length) {
      message.warning('No more questions');
      return;
    }

    try {
      setLoading(true);
      const nextQuestion = questions[nextIndex];

      await updateDoc(doc(db, COLLECTIONS.LIVE_TEST_SESSIONS, session.id), {
        current_question_index: nextIndex,
        current_question_id: nextQuestion.id,
        status: 'in_progress',
        question_start_time: new Date(),
      });

      setCurrentQuestion(nextQuestion);
      setResponseCount(0);
      setResponses([]);
      message.success(`Showing question ${nextIndex + 1} to students`);
    } catch (error) {
      message.error('Failed to move to next question');
    } finally {
      setLoading(false);
    }
  };

  const handleFinishTest = () => {
    Modal.confirm({
      title: 'Finish Live Test',
      content: 'Are you sure you want to end this test? All students will be redirected to results.',
      okText: 'Yes, Finish Test',
      okType: 'danger',
      onOk: async () => {
        try {
          const responsesSnapshot = await getDocs(
            query(
              collection(db, COLLECTIONS.LIVE_TEST_RESPONSES),
              where('session_id', '==', session!.id)
            )
          );

          const participantsSnapshot = await getDocs(
            query(
              collection(db, COLLECTIONS.LIVE_TEST_PARTICIPANTS),
              where('session_id', '==', session!.id)
            )
          );

          const participants = participantsSnapshot.docs.map((d) => {
            const data = d.data();
            return {
              id: d.id,
              student_id: data.student_id != null ? String(data.student_id) : '',
            };
          });

          const responses = responsesSnapshot.docs.map((d) => d.data());

          await axios.post(`${API_URL}/live-tests/save-results`, {
            session_id: session!.id,
            responses,
            participants,
          });

          await updateDoc(doc(db, COLLECTIONS.LIVE_TEST_SESSIONS, session!.id), {
            status: 'completed',
            ended_at: new Date(),
          });

          await updateDoc(doc(db, COLLECTIONS.LIVE_TESTS, liveTestId!), {
            status: 'completed',
            updated_at: new Date(),
          });

          message.success('Test finished successfully');
          navigate('/live-tests');
        } catch (error: unknown) {
          console.error('Error finishing test:', error);
          const detail = (error as { response?: { data?: { detail?: string; error?: string } } })?.response
            ?.data?.detail;
          const errMsg = (error as { response?: { data?: { error?: string } } })?.response?.data?.error;
          message.error(detail || errMsg || 'Failed to finish test');
        }
      },
    });
  };

  const participantColumns = [
    {
      title: 'Student Name',
      dataIndex: 'student_name',
      key: 'student_name',
    },
    {
      title: 'Email',
      dataIndex: 'student_email',
      key: 'student_email',
    },
    {
      title: 'Joined At',
      dataIndex: 'joined_at',
      key: 'joined_at',
      render: (date: any) => new Date(date.seconds * 1000).toLocaleTimeString(),
    },
    {
      title: 'Answered',
      dataIndex: 'total_answered',
      key: 'total_answered',
      render: (count: number) => (
        <Tag color="blue">
          {count} / {session?.current_question_index! + 1}
        </Tag>
      ),
    },
    {
      title: 'Current Q',
      key: 'current_answer',
      render: (_: any, record: Participant) => {
        const resp = responses.find((r: any) => r.participant_id === record.id || r.student_id === record.id);
        if (!resp) return <Tag icon={<MinusCircleOutlined />} color="default">Not Answered</Tag>;
        return resp.is_correct
          ? <Tag icon={<CheckCircleOutlined />} color="success">Correct</Tag>
          : <Tag icon={<CloseCircleOutlined />} color="error">Wrong</Tag>;
      },
    },
    {
      title: 'Status',
      dataIndex: 'is_active',
      key: 'is_active',
      render: (isActive: boolean) => (
        <Badge status={isActive ? 'success' : 'default'} text={isActive ? 'Active' : 'Inactive'} />
      ),
    },
  ];

  if (!liveTest) {
    return <div style={{ padding: 24 }}>Loading...</div>;
  }

  const currentQuestionNumber = (session?.current_question_index || -1) + 1;
  const progressPercent = questions.length > 0
    ? ((currentQuestionNumber / questions.length) * 100).toFixed(0)
    : 0;

  console.log('Session:', session);
  console.log('Questions length:', questions.length);
  console.log('Current question number:', currentQuestionNumber);

  return (
    <div style={{ padding: '24px' }}>
      <Card style={{ marginBottom: 24 }}>
        <Row gutter={16}>
          <Col span={12}>
            <Title level={3}>{liveTest.title}</Title>
            <Text type="secondary">{liveTest.subject} | {liveTest.duration_minutes} minutes</Text>
          </Col>
          <Col span={12} style={{ textAlign: 'right' }}>
            {!session ? (
              <Button
                type="primary"
                size="large"
                icon={<PlayCircleOutlined />}
                onClick={handleStartTest}
                loading={loading}
              >
                Start Live Test
              </Button>
            ) : session.status === 'completed' ? (
              <Tag color="success" style={{ fontSize: 16, padding: '8px 16px' }}>
                Test Completed
              </Tag>
            ) : (
              <Space>
                <Button
                  type="primary"
                  icon={<StepForwardOutlined />}
                  onClick={handleNextQuestion}
                  loading={loading}
                  disabled={currentQuestionNumber >= questions.length}
                >
                  {currentQuestionNumber === 0 ? 'Start First Question' : 'Next Question'}
                </Button>
                <Button
                  danger
                  icon={<StopOutlined />}
                  onClick={handleFinishTest}
                >
                  Finish Test
                </Button>
              </Space>
            )}
          </Col>
        </Row>
      </Card>

      {session && (
        <>
          <Row gutter={16} style={{ marginBottom: 24 }}>
            <Col span={4}>
              <Card>
                <Statistic
                  title="Participants"
                  value={participants.length}
                  prefix={<UserOutlined />}
                  valueStyle={{ color: '#3f8600' }}
                />
              </Card>
            </Col>
            <Col span={4}>
              <Card>
                <Statistic
                  title="Current Question"
                  value={currentQuestionNumber}
                  suffix={`/ ${questions.length}`}
                />
              </Card>
            </Col>
            <Col span={4}>
              <Card>
                <Statistic
                  title="Responded"
                  value={responseCount}
                  suffix={`/ ${participants.length}`}
                  prefix={<CheckCircleOutlined />}
                />
              </Card>
            </Col>
            <Col span={4}>
              <Card>
                <Statistic
                  title="✅ Correct"
                  value={responses.filter((r: any) => r.is_correct).length}
                  suffix={`/ ${responseCount}`}
                  valueStyle={{ color: '#10B981' }}
                />
              </Card>
            </Col>
            <Col span={4}>
              <Card>
                <Statistic
                  title="❌ Wrong"
                  value={responses.filter((r: any) => !r.is_correct).length}
                  suffix={`/ ${responseCount}`}
                  valueStyle={{ color: '#EF4444' }}
                />
              </Card>
            </Col>
            <Col span={4}>
              <Card>
                <Statistic title="Progress" value={progressPercent} suffix="%" />
                <Progress percent={Number(progressPercent)} showInfo={false} />
              </Card>
            </Col>
          </Row>

          {currentQuestion && (
            <Card title={`Question ${currentQuestionNumber}`} style={{ marginBottom: 24 }}>
              <div style={{
                background: '#F9FAFB',
                borderRadius: 12,
                padding: 16,
                marginBottom: 16,
                border: '1px solid #E5E7EB'
              }}>
                <LatexText text={currentQuestion.question_text} />
              </div>
              <Title level={5}>Options:</Title>
              {currentQuestion.options?.map((option: any, index: number) => (
                <div
                  key={option.id}
                  style={{
                    padding: '12px',
                    marginBottom: '8px',
                    backgroundColor: option.is_correct ? '#f6ffed' : '#F9FAFB',
                    border: option.is_correct ? '1px solid #52c41a' : '1px solid #E5E7EB',
                    borderRadius: '8px',
                  }}
                >
                  <div style={{ display: 'flex', gap: 8 }}>
                    <Text strong={option.is_correct} style={{ minWidth: 20 }}>
                      {String.fromCharCode(65 + index)}.
                    </Text>
                    <div style={{ flex: 1 }}>
                      <LatexText text={option.option_text} />
                      {option.is_correct && <Tag color="success" style={{ marginLeft: 8, marginTop: 6 }}>Correct</Tag>}
                    </div>
                  </div>
                </div>
              ))}
              <div style={{ marginTop: 16 }}>
                <Text type="secondary">
                  Students Responded: {responseCount} / {participants.length}
                </Text>
                <Progress
                  percent={participants.length > 0 ? (responseCount / participants.length) * 100 : 0}
                  status="active"
                  style={{ marginTop: 8 }}
                />
              </div>
            </Card>
          )}

          {session.status === 'waiting' && (
            <Alert
              message="Waiting for First Question"
              description="Click 'Start First Question' to begin the test. Students are waiting to join."
              type="warning"
              showIcon
              style={{ marginBottom: 24 }}
            />
          )}

          <Card title="Participants">
            <Table
              columns={participantColumns}
              dataSource={participants}
              rowKey="id"
              pagination={false}
            />
          </Card>
        </>
      )}
    </div>
  );
}
